/**
 * /app/admin/settings — GLOBAL admin Settings (PULSE-52).
 *
 * Admin role only. Single source of truth for trading parameters —
 * everything here applies to every account in the system. There is no
 * per-account override layer (PULSE-56 was reverted; user-managed
 * per-account stuff like API keys lives in `/app/settings`).
 *
 *   Sections (vertical sidebar):
 *     Position       — trailing_stop / dump / price_position
 *     DCA            — multistep ladder + bounce
 *     Regime         — phase × direction matrix + stress knobs
 *     Risk           — kill switches + sl_coin_block
 *     Discovery      — defaults + excluded_slots
 *     CrashGuard     — jsonb thresholds + events feed
 *     System flags   — legacy key/value `system_config`
 *
 * Editing model: per-section local draft + explicit Save button.
 * Live money — no auto-save on blur.
 */
import { useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import {
  type CrashGuardSettings,
  type TradingSettings,
  patchTradingSettings,
  updateCrashGuard,
  useConfig,
  useCrashGuard,
  useCrashGuardEvents,
  useTradingSettings,
  updateConfig,
  type ConfigCategory,
  type ConfigItem,
} from '@/hooks/useDashboardApi'
import { useAuthStore } from '@/stores/auth'
import { cn } from '@/lib/cn'
import { timeAgo } from '@/lib/format'

type SectionId =
  | 'position' | 'dca' | 'regime' | 'risk' | 'discovery'
  | 'crashguard' | 'system'

const SECTIONS: SectionId[] = [
  'position', 'dca', 'regime', 'risk', 'discovery',
  'crashguard', 'system',
]

// Fields the runtime actually reads today. The rest get a
// "not wired yet" badge so admins know the change is cosmetic. Mirrors
// `api/schemas/trading_settings.py::WIRED_FIELDS`.
const WIRED_FIELDS = new Set<keyof TradingSettings>([
  'trailing_stop_enabled', 'trailing_stop_activation_pct',
  'trailing_stop_distance_pct',
  'dump_protection_enabled', 'dump_threshold_pct', 'dump_window_minutes',
  'dump_action',
  'price_position_filter_enabled', 'price_position_long_block_above',
  'price_position_short_block_below', 'price_position_lookback_days',
  'dca_enabled', 'dca_step_mode', 'dca_steps_pct', 'dca_sizes_pct',
  'dca_max_steps', 'dca_leverage', 'dca_max_loss_pct',
  'dca_max_hold_minutes', 'dca_tp_pct', 'dca_disable_trailing',
  'dca_bounce_pct', 'dca_bounce_timeout_minutes', 'dca_bounce_from_step',
  'dca_disabled_directions_in_phase', 'stress_dca_block_enabled',
  'stress_oi_threshold', 'stress_oi_window_hours',
  'ml_direction_filter_signal_types',
  'discovery_excluded_slots',
])

export default function Settings() {
  const { t } = useTranslation('app')
  const isAdmin = useAuthStore((s) => s.isAdmin())
  const [section, setSection] = useState<SectionId>('position')

  const { data: trading, isLoading } = useTradingSettings()

  return (
    <div className="p-4 sm:p-6 max-w-[1280px]">
      <header className="mb-4">
        <h1 className="text-2xl font-semibold tracking-tight">
          {t('admin.settings.title')}
        </h1>
        <p className="text-sm text-textDim mt-1">
          {t('admin.settings.subtitle_global')}
        </p>
        {!isAdmin && (
          <p className="text-xs text-amber mt-2">
            {t('admin.settings.readonly_notice')}
          </p>
        )}
      </header>

      <div className="grid grid-cols-1 md:grid-cols-[200px_1fr] gap-4">
        {/* Sidebar nav */}
        <nav className="bg-ink border border-line p-2 self-start">
          <ul className="space-y-0.5">
            {SECTIONS.map((id) => (
              <li key={id}>
                <button
                  onClick={() => setSection(id)}
                  className={cn(
                    'w-full text-left px-3 py-2 text-sm transition-colors',
                    section === id
                      ? 'bg-elevated/60 text-text border-l-2 border-l-fire pl-[10px]'
                      : 'text-textDim hover:bg-elevated/30 border-l-2 border-l-transparent',
                  )}
                >
                  {t(`admin.settings.section.${id}`)}
                </button>
              </li>
            ))}
          </ul>
          {trading && (
            <p className="text-2xs text-textFaint mt-3 px-3 leading-relaxed">
              {t('admin.settings.last_updated', {
                who: trading.updated_by ?? '—',
                ago: timeAgo(trading.updated_at),
              })}
            </p>
          )}
        </nav>

        {/* Content */}
        <div className="min-w-0">
          {isLoading || !trading ? (
            <div className="bg-ink border border-line px-5 py-10 text-center text-textFaint text-sm">—</div>
          ) : (
            <>
              {section === 'position'   && <PositionSection   data={trading} canEdit={isAdmin} />}
              {section === 'dca'        && <DcaSection        data={trading} canEdit={isAdmin} />}
              {section === 'regime'     && <RegimeSection     data={trading} canEdit={isAdmin} />}
              {section === 'risk'       && <RiskSection       data={trading} canEdit={isAdmin} />}
              {section === 'discovery'  && <DiscoverySection  data={trading} canEdit={isAdmin} />}
              {section === 'crashguard' && <CrashGuardPanel   canEdit={isAdmin} />}
              {section === 'system'     && <SystemFlagsPanel  canEdit={isAdmin} />}
            </>
          )}
        </div>
      </div>
    </div>
  )
}

/* ─── Form scaffolding ─────────────────────────────────────────── */

// Shared section prop shape used by every tab. Trading parameters are
// admin-only and not per-account (PULSE-56 reverted).
interface SectionProps {
  data: TradingSettings
  canEdit: boolean
}


/**
 * Format any error shape into a human-readable string for footer rendering.
 *
 * Handles:
 *  - axios error with `response.data.detail` (FastAPI 422 returns an array
 *    of `{loc, msg, type, input}`, 400/500 typically a string)
 *  - `Error` instances (uses `.message`)
 *  - plain strings
 *  - everything else → JSON.stringify, falling back to String() if the
 *    value has a cycle.
 *
 * Without this, `setError(String(obj))` produces "[object Object]" in the
 * UI footer when the API returns a structured validation error.
 */
function formatError(e: unknown): string {
  // axios-style: peek inside response.data.detail first
  const detail = (e as { response?: { data?: { detail?: unknown } } })
    ?.response?.data?.detail
  if (detail !== undefined) return formatDetail(detail)

  if (typeof e === 'string') return e
  if (e instanceof Error) return e.message
  return safeStringify(e)
}

function formatDetail(d: unknown): string {
  if (typeof d === 'string') return d
  if (Array.isArray(d)) {
    return d
      .map((x) => {
        if (typeof x === 'string') return x
        if (x && typeof x === 'object' && 'msg' in x) {
          const loc = 'loc' in x && Array.isArray((x as { loc: unknown[] }).loc)
            ? (x as { loc: unknown[] }).loc.join('.')
            : ''
          const msg = String((x as { msg: unknown }).msg)
          return loc ? `${loc}: ${msg}` : msg
        }
        return safeStringify(x)
      })
      .join('; ')
  }
  if (d && typeof d === 'object' && 'msg' in d) {
    return String((d as { msg: unknown }).msg)
  }
  return safeStringify(d)
}

function safeStringify(v: unknown): string {
  try { return JSON.stringify(v) }
  catch { return String(v) }
}

function useTradingForm(
  data: TradingSettings,
  fields: (keyof TradingSettings)[],
) {
  const qc = useQueryClient()
  const initial = useMemo(() => {
    const out: Partial<TradingSettings> = {}
    for (const k of fields) (out as Record<string, unknown>)[k] = data[k]
    return out
  }, [data, fields])
  const [draft, setDraft] = useState<Partial<TradingSettings>>(initial)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // If the server data refreshes (e.g. after invalidation) and we're not
  // sitting on user edits, fold the new values in.
  const dirty = fields.some((k) => !deepEqual(draft[k], data[k]))

  const set = <K extends keyof TradingSettings>(key: K, value: TradingSettings[K]) => {
    setDraft((d) => ({ ...d, [key]: value }))
  }

  const save = async () => {
    if (!dirty) return
    setSaving(true); setError(null)
    try {
      const patch: Partial<TradingSettings> = {}
      for (const k of fields) {
        if (!deepEqual(draft[k], data[k])) (patch as Record<string, unknown>)[k] = draft[k]
      }
      await patchTradingSettings(patch)
      qc.invalidateQueries({ queryKey: ['admin', 'config', 'trading'] })
    } catch (e) {
      setError(formatError(e))
    } finally {
      setSaving(false)
    }
  }

  const reset = () => setDraft(initial)

  return { draft, set, save, reset, saving, error, dirty }
}

function deepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false
    for (let i = 0; i < a.length; i++) if (!deepEqual(a[i], b[i])) return false
    return true
  }
  if (typeof a === 'object' && typeof b === 'object' && a && b) {
    const ak = Object.keys(a as object).sort()
    const bk = Object.keys(b as object).sort()
    if (ak.length !== bk.length) return false
    return ak.every((k, i) => k === bk[i] &&
      deepEqual((a as Record<string, unknown>)[k], (b as Record<string, unknown>)[k]))
  }
  return false
}

/* ─── Section components ───────────────────────────────────────── */

function SectionCard({
  title, hint, children, dirty, saving, error, onSave, onReset, canEdit,
}: {
  title: string
  hint?: string
  children: React.ReactNode
  dirty: boolean
  saving: boolean
  error: string | null
  onSave: () => void
  onReset: () => void
  canEdit: boolean
}) {
  const { t } = useTranslation('app')
  return (
    <section className="bg-ink border border-line">
      <header className="px-5 py-3 border-b border-line">
        <h2 className="text-sm font-semibold tracking-tight">{title}</h2>
        {hint && <p className="text-xs text-textDim mt-1">{hint}</p>}
      </header>
      <div className="px-5 py-4 space-y-4">{children}</div>
      <footer className="px-5 py-3 border-t border-line/60 flex items-center gap-3">
        {error && <span className="text-xs text-rose break-all">{error}</span>}
        {dirty && !error && (
          <span className="text-xs text-amber">
            {t('admin.settings.unsaved_changes')}
          </span>
        )}
        <div className="ml-auto flex gap-2">
          <Button
            size="sm"
            variant="secondary"
            disabled={!dirty || saving}
            onClick={onReset}
          >
            {t('admin.settings.reset')}
          </Button>
          <Button
            size="sm"
            variant="primary"
            disabled={!dirty || saving || !canEdit}
            onClick={onSave}
          >
            {saving ? t('admin.settings.saving') : t('admin.settings.save')}
          </Button>
        </div>
      </footer>
    </section>
  )
}

function Field({
  label, hint, wired = true, children,
}: {
  label: string
  hint?: string
  wired?: boolean
  children: React.ReactNode
}) {
  const { t } = useTranslation('app')
  return (
    <div className="grid grid-cols-1 md:grid-cols-[1fr_280px] gap-3 items-start">
      <div>
        <div className="flex items-center gap-2">
          <label className="text-xs uppercase tracking-wider text-textDim font-medium">
            {label}
          </label>
          {!wired && (
            <span
              className="pill bg-elevated text-textFaint text-2xs"
              title={t('admin.settings.not_wired_tooltip')}
            >
              {t('admin.settings.not_wired')}
            </span>
          )}
        </div>
        {hint && <p className="text-2xs text-textFaint mt-0.5">{hint}</p>}
      </div>
      <div>{children}</div>
    </div>
  )
}

function NumInput({
  value, onChange, step = 0.1, min, max, disabled,
}: {
  value: number | null
  onChange: (n: number | null) => void
  step?: number; min?: number; max?: number
  disabled?: boolean
}) {
  return (
    <Input
      type="number"
      step={step} min={min} max={max} disabled={disabled}
      value={value == null ? '' : value}
      onChange={(e) => {
        const v = e.target.value
        onChange(v === '' ? null : Number(v))
      }}
      className="font-mono"
    />
  )
}

function BoolSwitch({
  value, onChange, disabled,
}: { value: boolean; onChange: (v: boolean) => void; disabled?: boolean }) {
  return (
    <Button
      size="sm"
      variant={value ? 'primary' : 'secondary'}
      disabled={disabled}
      onClick={() => onChange(!value)}
      className="w-full"
    >
      {value ? 'TRUE' : 'FALSE'}
    </Button>
  )
}

function NumberListInput({
  value, onChange, disabled,
}: {
  value: number[]
  onChange: (arr: number[]) => void
  disabled?: boolean
}) {
  const [raw, setRaw] = useState(value.join(', '))
  // re-sync if the upstream value changes from a successful save
  const valueStr = value.join(', ')
  if (valueStr !== raw && !raw.endsWith(',') && !raw.endsWith(' ')) {
    // Cheap drift detection — only resync when not mid-typing.
    // (No effect, only on render; safe because input is controlled below.)
  }
  return (
    <Input
      type="text"
      disabled={disabled}
      value={raw}
      onChange={(e) => setRaw(e.target.value)}
      onBlur={() => {
        const nums = raw.split(',')
          .map((s) => s.trim()).filter(Boolean).map(Number)
          .filter((n) => Number.isFinite(n) && n > 0)
        onChange(nums)
        setRaw(nums.join(', '))
      }}
      placeholder="5, 12, 20"
      className="font-mono"
    />
  )
}

function StringListInput({
  value, onChange, disabled,
}: {
  value: string[]
  onChange: (arr: string[]) => void
  disabled?: boolean
}) {
  const [raw, setRaw] = useState(value.join(', '))
  return (
    <Input
      type="text"
      disabled={disabled}
      value={raw}
      onChange={(e) => setRaw(e.target.value)}
      onBlur={() => {
        const items = raw.split(',')
          .map((s) => s.trim()).filter(Boolean)
        onChange(items)
        setRaw(items.join(', '))
      }}
      placeholder="sdump_reversal, bullish_div_counter"
      className="font-mono text-xs"
    />
  )
}

/* ── Position section ─────────────────────────────────────────── */

export function PositionSection({ data, canEdit }: SectionProps) {
  const { t } = useTranslation('app')
  const f = useTradingForm(data, [
    'trailing_stop_enabled', 'trailing_stop_activation_pct', 'trailing_stop_distance_pct',
    'dump_protection_enabled', 'dump_threshold_pct', 'dump_window_minutes', 'dump_action',
    'price_position_filter_enabled', 'price_position_long_block_above',
    'price_position_short_block_below', 'price_position_lookback_days',
  ])

  return (
    <SectionCard
      title={t('admin.settings.position.title')}
      hint={t('admin.settings.position.hint')}
      dirty={f.dirty} saving={f.saving} error={f.error}
      onSave={f.save} onReset={f.reset} canEdit={canEdit}
    >
      {/* Trailing stop */}
      <Field label={t('admin.settings.f.trailing_stop_enabled')}
             hint={t('admin.settings.f.trailing_stop_enabled_hint')}>
        <BoolSwitch
          value={!!f.draft.trailing_stop_enabled}
          onChange={(v) => f.set('trailing_stop_enabled', v)}
          disabled={!canEdit}
        />
      </Field>
      {f.draft.trailing_stop_enabled && (
        <>
          <Field label={t('admin.settings.f.trailing_activation_pct')}
                 hint={t('admin.settings.f.trailing_activation_pct_hint')}>
            <NumInput
              value={f.draft.trailing_stop_activation_pct ?? 0}
              onChange={(v) => f.set('trailing_stop_activation_pct', v ?? 0)}
              step={0.1} min={0.1} disabled={!canEdit}
            />
          </Field>
          <Field label={t('admin.settings.f.trailing_distance_pct')}
                 hint={t('admin.settings.f.trailing_distance_pct_hint')}>
            <NumInput
              value={f.draft.trailing_stop_distance_pct ?? 0}
              onChange={(v) => f.set('trailing_stop_distance_pct', v ?? 0)}
              step={0.1} min={0.1} disabled={!canEdit}
            />
          </Field>
        </>
      )}

      <Divider />

      {/* Dump protection */}
      <Field label={t('admin.settings.f.dump_protection_enabled')}
             hint={t('admin.settings.f.dump_protection_enabled_hint')}>
        <BoolSwitch
          value={!!f.draft.dump_protection_enabled}
          onChange={(v) => f.set('dump_protection_enabled', v)}
          disabled={!canEdit}
        />
      </Field>
      {f.draft.dump_protection_enabled && (
        <>
          <Field label={t('admin.settings.f.dump_threshold_pct')}>
            <NumInput
              value={f.draft.dump_threshold_pct ?? 0}
              onChange={(v) => f.set('dump_threshold_pct', v ?? 0)}
              step={0.1} min={0.1} disabled={!canEdit}
            />
          </Field>
          <Field label={t('admin.settings.f.dump_window_minutes')}>
            <NumInput
              value={f.draft.dump_window_minutes ?? 0}
              onChange={(v) => f.set('dump_window_minutes', v ?? 0)}
              step={1} min={1} disabled={!canEdit}
            />
          </Field>
          <Field label={t('admin.settings.f.dump_action')}>
            <Select
              value={f.draft.dump_action ?? 'close'}
              disabled={!canEdit}
              onChange={(e) => f.set('dump_action',
                e.target.value as 'close' | 'skip_dca')}
            >
              <option value="close">close</option>
              <option value="skip_dca">skip_dca</option>
            </Select>
          </Field>
        </>
      )}

      <Divider />

      {/* Price position filter */}
      <Field label={t('admin.settings.f.price_position_filter_enabled')}
             hint={t('admin.settings.f.price_position_filter_hint')}>
        <BoolSwitch
          value={!!f.draft.price_position_filter_enabled}
          onChange={(v) => f.set('price_position_filter_enabled', v)}
          disabled={!canEdit}
        />
      </Field>
      {f.draft.price_position_filter_enabled && (
        <>
          <Field label={t('admin.settings.f.price_position_long_block_above')}>
            <NumInput
              value={f.draft.price_position_long_block_above ?? 0}
              onChange={(v) => f.set('price_position_long_block_above', v ?? 0)}
              step={0.05} min={0.5} max={0.99} disabled={!canEdit}
            />
          </Field>
          <Field label={t('admin.settings.f.price_position_short_block_below')}>
            <NumInput
              value={f.draft.price_position_short_block_below ?? 0}
              onChange={(v) => f.set('price_position_short_block_below', v ?? 0)}
              step={0.05} min={0.01} max={0.5} disabled={!canEdit}
            />
          </Field>
          <Field label={t('admin.settings.f.price_position_lookback_days')}>
            <NumInput
              value={f.draft.price_position_lookback_days ?? 0}
              onChange={(v) => f.set('price_position_lookback_days', v ?? 0)}
              step={1} min={1} max={30} disabled={!canEdit}
            />
          </Field>
        </>
      )}
    </SectionCard>
  )
}

/* ── DCA section ──────────────────────────────────────────────── */

export function DcaSection({ data, canEdit }: SectionProps) {
  const { t } = useTranslation('app')
  const f = useTradingForm(data, [
    'dca_enabled', 'dca_step_mode', 'dca_steps_pct', 'dca_sizes_pct',
    'dca_max_steps', 'dca_leverage', 'dca_max_loss_pct',
    'dca_max_hold_minutes', 'dca_tp_pct', 'dca_disable_trailing',
    'dca_bounce_pct', 'dca_bounce_timeout_minutes', 'dca_bounce_from_step',
  ])

  return (
    <SectionCard
      title={t('admin.settings.dca.title')}
      hint={t('admin.settings.dca.hint')}
      dirty={f.dirty} saving={f.saving} error={f.error}
      onSave={f.save} onReset={f.reset} canEdit={canEdit}
    >
      <Field label={t('admin.settings.f.dca_enabled')}>
        <BoolSwitch
          value={!!f.draft.dca_enabled}
          onChange={(v) => f.set('dca_enabled', v)}
          disabled={!canEdit}
        />
      </Field>

      {f.draft.dca_enabled && (
        <>
          <Field label={t('admin.settings.f.dca_step_mode')}
                 hint={t('admin.settings.f.dca_step_mode_hint')}>
            <Select
              value={f.draft.dca_step_mode ?? 'multistep'}
              disabled={!canEdit}
              onChange={(e) => f.set('dca_step_mode',
                e.target.value as 'multistep' | 'legacy')}
            >
              <option value="multistep">multistep</option>
              <option value="legacy">legacy</option>
            </Select>
          </Field>

          <Field label={t('admin.settings.f.dca_steps_pct')}
                 hint={t('admin.settings.f.dca_steps_pct_hint')}>
            <NumberListInput
              value={f.draft.dca_steps_pct ?? []}
              onChange={(v) => f.set('dca_steps_pct', v)}
              disabled={!canEdit}
            />
          </Field>

          <Field label={t('admin.settings.f.dca_sizes_pct')}
                 hint={t('admin.settings.f.dca_sizes_pct_hint')}>
            <NumberListInput
              value={f.draft.dca_sizes_pct ?? []}
              onChange={(v) => f.set('dca_sizes_pct', v)}
              disabled={!canEdit}
            />
          </Field>

          <Field label={t('admin.settings.f.dca_max_steps')}>
            <NumInput
              value={f.draft.dca_max_steps ?? 0}
              onChange={(v) => f.set('dca_max_steps', v ?? 0)}
              step={1} min={1} max={12} disabled={!canEdit}
            />
          </Field>

          <Field label={t('admin.settings.f.dca_leverage')}>
            <NumInput
              value={f.draft.dca_leverage ?? 0}
              onChange={(v) => f.set('dca_leverage', v ?? 0)}
              step={1} min={1} max={50} disabled={!canEdit}
            />
          </Field>

          <Field label={t('admin.settings.f.dca_tp_pct')}
                 hint={t('admin.settings.f.dca_tp_pct_hint')}>
            <NumInput
              value={f.draft.dca_tp_pct ?? 0}
              onChange={(v) => f.set('dca_tp_pct', v ?? 0)}
              step={0.1} min={0} disabled={!canEdit}
            />
          </Field>

          <Field label={t('admin.settings.f.dca_max_loss_pct')}>
            <NumInput
              value={f.draft.dca_max_loss_pct ?? 0}
              onChange={(v) => f.set('dca_max_loss_pct', v ?? 0)}
              step={0.5} min={0} disabled={!canEdit}
            />
          </Field>

          <Field label={t('admin.settings.f.dca_max_hold_minutes')}>
            <NumInput
              value={f.draft.dca_max_hold_minutes ?? 0}
              onChange={(v) => f.set('dca_max_hold_minutes', v ?? 0)}
              step={5} min={1} disabled={!canEdit}
            />
          </Field>

          <Field label={t('admin.settings.f.dca_disable_trailing')}>
            <BoolSwitch
              value={!!f.draft.dca_disable_trailing}
              onChange={(v) => f.set('dca_disable_trailing', v)}
              disabled={!canEdit}
            />
          </Field>

          <Divider />

          <Field label={t('admin.settings.f.dca_bounce_pct')}
                 hint={t('admin.settings.f.dca_bounce_pct_hint')}>
            <NumInput
              value={f.draft.dca_bounce_pct ?? null}
              onChange={(v) => f.set('dca_bounce_pct', v)}
              step={0.1} min={0} disabled={!canEdit}
            />
          </Field>

          <Field label={t('admin.settings.f.dca_bounce_timeout_minutes')}>
            <NumInput
              value={f.draft.dca_bounce_timeout_minutes ?? 0}
              onChange={(v) => f.set('dca_bounce_timeout_minutes', v ?? 0)}
              step={1} min={1} disabled={!canEdit}
            />
          </Field>

          <Field label={t('admin.settings.f.dca_bounce_from_step')}>
            <NumInput
              value={f.draft.dca_bounce_from_step ?? 0}
              onChange={(v) => f.set('dca_bounce_from_step', v ?? 0)}
              step={1} min={1} disabled={!canEdit}
            />
          </Field>
        </>
      )}
    </SectionCard>
  )
}

/* ── Regime section ───────────────────────────────────────────── */

export function RegimeSection({ data, canEdit }: SectionProps) {
  const { t } = useTranslation('app')
  const f = useTradingForm(data, [
    'dca_disabled_directions_in_phase', 'stress_dca_block_enabled',
    'stress_oi_threshold', 'stress_oi_window_hours',
    'ml_direction_filter_signal_types',
  ])

  // Editor for the phase → directions map. Phases are fixed, directions
  // toggle per-phase. Stored as Record<phase, ['long', 'short']>.
  const phases = ['bullish', 'consolidation', 'bearish', 'recovery', 'capitulation']
  const dirs = ['long', 'short'] as const
  const map = (f.draft.dca_disabled_directions_in_phase ?? {}) as Record<string, string[]>

  const toggleDirInPhase = (phase: string, dir: string) => {
    const cur = new Set(map[phase] ?? [])
    if (cur.has(dir)) cur.delete(dir); else cur.add(dir)
    const next = { ...map }
    if (cur.size === 0) delete next[phase]
    else next[phase] = Array.from(cur)
    f.set('dca_disabled_directions_in_phase', next)
  }

  return (
    <SectionCard
      title={t('admin.settings.regime.title')}
      hint={t('admin.settings.regime.hint')}
      dirty={f.dirty} saving={f.saving} error={f.error}
      onSave={f.save} onReset={f.reset} canEdit={canEdit}
    >
      {/* DCA-disabled directions per BTC phase */}
      <Field label={t('admin.settings.f.disabled_directions_in_phase')}
             hint={t('admin.settings.f.disabled_directions_in_phase_hint')}>
        <div className="space-y-2 col-span-2">
          {phases.map((phase) => (
            <div key={phase}
                 className="flex items-center gap-3 text-xs font-mono">
              <span className="w-32 text-textDim uppercase">{phase}</span>
              {dirs.map((d) => {
                const on = (map[phase] ?? []).includes(d)
                return (
                  <button
                    key={d}
                    disabled={!canEdit}
                    onClick={() => toggleDirInPhase(phase, d)}
                    className={cn(
                      'px-2 py-1 border uppercase',
                      on
                        ? 'bg-rose-faint text-rose border-rose'
                        : 'bg-elevated text-textDim border-line',
                    )}
                  >
                    {d} {on ? '✗' : '·'}
                  </button>
                )
              })}
            </div>
          ))}
        </div>
      </Field>

      <Divider />

      <Field label={t('admin.settings.f.stress_dca_block_enabled')}
             hint={t('admin.settings.f.stress_dca_block_enabled_hint')}>
        <BoolSwitch
          value={!!f.draft.stress_dca_block_enabled}
          onChange={(v) => f.set('stress_dca_block_enabled', v)}
          disabled={!canEdit}
        />
      </Field>

      <Field label={t('admin.settings.f.stress_oi_threshold')}
             hint={t('admin.settings.f.stress_oi_threshold_hint')}>
        <NumInput
          value={f.draft.stress_oi_threshold ?? null}
          onChange={(v) => f.set('stress_oi_threshold', v)}
          step={1} min={1} disabled={!canEdit}
        />
      </Field>

      <Field label={t('admin.settings.f.stress_oi_window_hours')}>
        <NumInput
          value={f.draft.stress_oi_window_hours ?? null}
          onChange={(v) => f.set('stress_oi_window_hours', v)}
          step={1} min={1} disabled={!canEdit}
        />
      </Field>

      <Divider />

      <Field label={t('admin.settings.f.ml_direction_filter_signal_types')}
             hint={t('admin.settings.f.ml_direction_filter_signal_types_hint')}>
        <StringListInput
          value={f.draft.ml_direction_filter_signal_types ?? []}
          onChange={(v) => f.set('ml_direction_filter_signal_types', v)}
          disabled={!canEdit}
        />
      </Field>
    </SectionCard>
  )
}

/* ── Risk section ─────────────────────────────────────────────── */

export function RiskSection({ data, canEdit }: SectionProps) {
  const { t } = useTranslation('app')
  const f = useTradingForm(data, [
    'global_position_size_pct', 'global_leverage_override',
    'cb_global_daily_loss_pct',
    'sl_coin_block_enabled', 'sl_coin_block_minutes',
    'per_symbol_daily_loss_limit',
  ])

  // PULSE-66: global sizing override toggles. The DB columns are
  // nullable — NULL means "use per-strategy", a value means "force this
  // for every entry". Keep two toggles for clarity in the UI; when the
  // toggle is OFF we write null to the patch (clearing the override).
  const sizingOn = f.draft.global_position_size_pct != null
  const leverageOn = f.draft.global_leverage_override != null

  return (
    <SectionCard
      title={t('admin.settings.risk.title')}
      hint={t('admin.settings.risk.hint')}
      dirty={f.dirty} saving={f.saving} error={f.error}
      onSave={f.save} onReset={f.reset} canEdit={canEdit}
    >
      {/* ─── Global sizing override (PULSE-66) ──────────────────── */}
      <Field label={t('admin.settings.f.global_position_size_pct_enabled')}
             hint={t('admin.settings.f.global_position_size_pct_enabled_hint')}
             wired={WIRED_FIELDS.has('global_position_size_pct')}>
        <BoolSwitch
          value={sizingOn}
          onChange={(v) => f.set(
            'global_position_size_pct',
            v ? (Number(data.global_position_size_pct ?? 5.0)) : null,
          )}
          disabled={!canEdit}
        />
      </Field>
      {sizingOn && (
        <Field label={t('admin.settings.f.global_position_size_pct')}
               hint={t('admin.settings.f.global_position_size_pct_hint')}
               wired={WIRED_FIELDS.has('global_position_size_pct')}>
          <NumInput
            value={f.draft.global_position_size_pct ?? 0}
            onChange={(v) => f.set('global_position_size_pct', v ?? 0)}
            step={0.5} min={0.1} max={100} disabled={!canEdit}
          />
        </Field>
      )}

      <Field label={t('admin.settings.f.global_leverage_override_enabled')}
             hint={t('admin.settings.f.global_leverage_override_enabled_hint')}
             wired={WIRED_FIELDS.has('global_leverage_override')}>
        <BoolSwitch
          value={leverageOn}
          onChange={(v) => f.set(
            'global_leverage_override',
            v ? (Number(data.global_leverage_override ?? 5)) : null,
          )}
          disabled={!canEdit}
        />
      </Field>
      {leverageOn && (
        <Field label={t('admin.settings.f.global_leverage_override')}
               hint={t('admin.settings.f.global_leverage_override_hint')}
               wired={WIRED_FIELDS.has('global_leverage_override')}>
          <NumInput
            value={f.draft.global_leverage_override ?? 0}
            onChange={(v) => f.set('global_leverage_override', v ?? 0)}
            step={1} min={1} max={100} disabled={!canEdit}
          />
        </Field>
      )}

      <Divider />

      <Field label={t('admin.settings.f.cb_global_daily_loss_pct')}
             hint={t('admin.settings.f.cb_global_daily_loss_pct_hint')}
             wired={WIRED_FIELDS.has('cb_global_daily_loss_pct')}>
        <NumInput
          value={f.draft.cb_global_daily_loss_pct ?? 0}
          onChange={(v) => f.set('cb_global_daily_loss_pct', v ?? 0)}
          step={0.5} max={0} disabled={!canEdit}
        />
      </Field>

      <Divider />

      <Field label={t('admin.settings.f.sl_coin_block_enabled')}
             hint={t('admin.settings.f.sl_coin_block_enabled_hint')}
             wired={WIRED_FIELDS.has('sl_coin_block_enabled')}>
        <BoolSwitch
          value={!!f.draft.sl_coin_block_enabled}
          onChange={(v) => f.set('sl_coin_block_enabled', v)}
          disabled={!canEdit}
        />
      </Field>
      {f.draft.sl_coin_block_enabled && (
        <Field label={t('admin.settings.f.sl_coin_block_minutes')}
               wired={WIRED_FIELDS.has('sl_coin_block_minutes')}>
          <NumInput
            value={f.draft.sl_coin_block_minutes ?? 0}
            onChange={(v) => f.set('sl_coin_block_minutes', v ?? 0)}
            step={5} min={5} max={1440} disabled={!canEdit}
          />
        </Field>
      )}

      <Divider />

      <Field label={t('admin.settings.f.per_symbol_daily_loss_limit')}
             hint={t('admin.settings.f.per_symbol_daily_loss_limit_hint')}
             wired={WIRED_FIELDS.has('per_symbol_daily_loss_limit')}>
        <NumInput
          value={f.draft.per_symbol_daily_loss_limit ?? null}
          onChange={(v) => f.set('per_symbol_daily_loss_limit', v)}
          step={0.5} max={0} disabled={!canEdit}
        />
      </Field>
    </SectionCard>
  )
}

/* ── Discovery section ────────────────────────────────────────── */

export function DiscoverySection({ data, canEdit }: SectionProps) {
  const { t } = useTranslation('app')
  const f = useTradingForm(data, [
    'discovery_default_leverage', 'discovery_default_position_size_pct',
    'discovery_excluded_slots',
  ])

  return (
    <SectionCard
      title={t('admin.settings.discovery.title')}
      hint={t('admin.settings.discovery.hint')}
      dirty={f.dirty} saving={f.saving} error={f.error}
      onSave={f.save} onReset={f.reset} canEdit={canEdit}
    >
      <Field label={t('admin.settings.f.discovery_default_leverage')}
             hint={t('admin.settings.f.discovery_default_leverage_hint')}
             wired={WIRED_FIELDS.has('discovery_default_leverage')}>
        <NumInput
          value={f.draft.discovery_default_leverage ?? 0}
          onChange={(v) => f.set('discovery_default_leverage', v ?? 0)}
          step={1} min={1} max={50} disabled={!canEdit}
        />
      </Field>

      <Field label={t('admin.settings.f.discovery_default_position_size_pct')}
             wired={WIRED_FIELDS.has('discovery_default_position_size_pct')}>
        <NumInput
          value={f.draft.discovery_default_position_size_pct ?? 0}
          onChange={(v) => f.set('discovery_default_position_size_pct', v ?? 0)}
          step={0.1} min={0.1} disabled={!canEdit}
        />
      </Field>

      <Divider />

      <Field label={t('admin.settings.f.discovery_excluded_slots')}
             hint={t('admin.settings.f.discovery_excluded_slots_hint')}>
        <StringListInput
          value={f.draft.discovery_excluded_slots ?? []}
          onChange={(v) => f.set('discovery_excluded_slots', v)}
          disabled={!canEdit}
        />
      </Field>
    </SectionCard>
  )
}

function Divider() {
  return <div className="border-t border-line/40" />
}

/* ─── CrashGuard panel ─────────────────────────────────────────── */

function CrashGuardPanel({ canEdit }: { canEdit: boolean }) {
  const { t } = useTranslation('app')
  const qc = useQueryClient()
  const { data, isLoading } = useCrashGuard()
  const { data: events } = useCrashGuardEvents(7, 20)
  const [draft, setDraft] = useState<CrashGuardSettings | null>(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  if (isLoading || !data) return <div className="px-5 py-10 text-textFaint">—</div>
  const current = draft ?? data.settings
  const dirty = JSON.stringify(current) !== JSON.stringify(data.settings)

  const set = <K extends keyof CrashGuardSettings>(
    key: K, value: CrashGuardSettings[K],
  ) => {
    setDraft({ ...current, [key]: value })
  }

  const save = async () => {
    setSaving(true); setError(null)
    try {
      await updateCrashGuard(current)
      qc.invalidateQueries({ queryKey: ['admin', 'config', 'crashguard'] })
      setDraft(null)
    } catch (e) {
      setError(formatError(e))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-4">
      <SectionCard
        title={t('admin.settings.crashguard.title')}
        hint={t('admin.settings.crashguard.hint')}
        dirty={dirty} saving={saving} error={error}
        onSave={save} onReset={() => setDraft(null)}
        canEdit={canEdit}
      >
        <div className="flex items-center gap-4 text-xs font-mono mb-2">
          <span className={cn(
            'pill text-2xs',
            data.settings.enabled
              ? 'bg-pulse-faint text-pulse'
              : 'bg-elevated text-textFaint',
          )}>
            {data.settings.enabled ? 'ACTIVE' : 'DISABLED'}
          </span>
          {data.events_today > 0 && (
            <span className="pill text-2xs bg-amber-faint text-amber">
              {t('admin.settings.crashguard.events_today', {
                count: data.events_today,
              })}
            </span>
          )}
          {data.last_trigger && (
            <span className="text-textFaint">
              {t('admin.settings.crashguard.last_trigger')}{' '}
              {timeAgo(data.last_trigger)}
            </span>
          )}
        </div>

        <Field label={t('admin.settings.crashguard.f.enabled')}>
          <BoolSwitch
            value={current.enabled}
            onChange={(v) => set('enabled', v)}
            disabled={!canEdit}
          />
        </Field>

        <Field label={t('admin.settings.crashguard.f.oi_drop_pct')}>
          <NumInput value={current.oi_drop_pct}
            onChange={(v) => set('oi_drop_pct', v ?? 0)}
            step={0.5} disabled={!canEdit} />
        </Field>
        <Field label={t('admin.settings.crashguard.f.liq_cascade_usd')}>
          <NumInput value={current.liq_cascade_usd}
            onChange={(v) => set('liq_cascade_usd', v ?? 0)}
            step={100_000} disabled={!canEdit} />
        </Field>
        <Field label={t('admin.settings.crashguard.f.volume_spike_multiplier')}>
          <NumInput value={current.volume_spike_multiplier}
            onChange={(v) => set('volume_spike_multiplier', v ?? 0)}
            step={0.5} disabled={!canEdit} />
        </Field>
        <Field label={t('admin.settings.crashguard.f.funding_threshold_bps')}>
          <NumInput value={current.funding_threshold_bps}
            onChange={(v) => set('funding_threshold_bps', v ?? 0)}
            step={0.1} disabled={!canEdit} />
        </Field>
        <Field label={t('admin.settings.crashguard.f.squeeze_liq_ratio')}>
          <NumInput value={current.squeeze_liq_ratio}
            onChange={(v) => set('squeeze_liq_ratio', v ?? 0)}
            step={0.1} disabled={!canEdit} />
        </Field>
        <Field label={t('admin.settings.crashguard.f.cvd_reversal_negative')}>
          <NumInput value={current.cvd_reversal_negative}
            onChange={(v) => set('cvd_reversal_negative', v ?? 0)}
            step={1_000_000} disabled={!canEdit} />
        </Field>
        <Field label={t('admin.settings.crashguard.f.cvd_reversal_positive')}>
          <NumInput value={current.cvd_reversal_positive}
            onChange={(v) => set('cvd_reversal_positive', v ?? 0)}
            step={1_000_000} disabled={!canEdit} />
        </Field>

        <Divider />

        <Field label={t('admin.settings.crashguard.f.block_direction')}>
          <Select
            value={current.block_direction}
            disabled={!canEdit}
            onChange={(e) => set('block_direction',
              e.target.value as 'long' | 'short' | 'both')}
          >
            <option value="short">SHORT only</option>
            <option value="long">LONG only</option>
            <option value="both">both</option>
          </Select>
        </Field>
        <Field label={t('admin.settings.crashguard.f.block_duration_minutes')}>
          <NumInput value={current.block_duration_minutes}
            onChange={(v) => set('block_duration_minutes', v ?? 0)}
            step={5} min={1} disabled={!canEdit} />
        </Field>
        <Field label={t('admin.settings.crashguard.f.min_triggers')}
               hint={t('admin.settings.crashguard.f.min_triggers_hint')}>
          <NumInput value={current.min_triggers}
            onChange={(v) => set('min_triggers', v ?? 1)}
            step={1} min={1} max={6} disabled={!canEdit} />
        </Field>
      </SectionCard>

      {/* Recent events */}
      <section className="bg-ink border border-line">
        <header className="px-5 py-3 border-b border-line">
          <h2 className="text-sm font-semibold tracking-tight">
            {t('admin.settings.crashguard.events_title')}
          </h2>
          <p className="text-xs text-textDim mt-1">
            {t('admin.settings.crashguard.events_hint')}
          </p>
        </header>
        {!events || events.length === 0 ? (
          <div className="px-5 py-6 text-textFaint text-sm">—</div>
        ) : (
          <ul className="divide-y divide-line/40 font-mono text-xs">
            {events.map((e) => (
              <li key={e.id} className="px-5 py-2 flex items-center gap-3">
                <span className="text-textFaint w-32">
                  {new Date(e.created_at).toLocaleString()}
                </span>
                <span className="pill text-2xs bg-rose-faint text-rose">
                  {e.trigger_type}
                </span>
                <span className="text-text flex-1 truncate"
                      title={e.description ?? undefined}>
                  {e.description ?? '—'}
                </span>
                {e.blocked_symbol && (
                  <span className="text-textDim">{e.blocked_symbol}</span>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  )
}

/* ─── System flags panel (legacy system_config) ────────────────── */

const CATEGORY_ORDER: ConfigCategory[] = [
  'flags', 'risk', 'detector', 'ml', 'integrations',
]

function SystemFlagsPanel({ canEdit }: { canEdit: boolean }) {
  const { t } = useTranslation('app')
  const { data, isLoading } = useConfig()
  const grouped = useMemo(() => {
    const map = new Map<ConfigCategory, ConfigItem[]>()
    for (const c of CATEGORY_ORDER) map.set(c, [])
    for (const it of data?.items ?? []) {
      const arr = map.get(it.category); if (arr) arr.push(it)
    }
    return map
  }, [data])

  if (isLoading || !data) {
    return <div className="bg-ink border border-line px-5 py-10 text-center text-textFaint text-sm">—</div>
  }

  return (
    <div className="space-y-4">
      {CATEGORY_ORDER.map((cat) => {
        const items = grouped.get(cat) ?? []
        if (items.length === 0) return null
        return (
          <section key={cat} className="bg-ink border border-line">
            <header className="px-5 py-3 border-b border-line flex items-baseline">
              <h2 className="text-sm font-semibold tracking-tight">
                {t(`admin.settings.category.${cat}`)}
              </h2>
              <span className="ml-auto text-xs text-textFaint font-mono">
                {items.length} {t('admin.settings.keys')}
              </span>
            </header>
            <ul className="divide-y divide-line/40">
              {items.map((it) => (
                <SystemConfigRow key={it.key} item={it} canEdit={canEdit} />
              ))}
            </ul>
          </section>
        )
      })}
    </div>
  )
}

function SystemConfigRow({
  item, canEdit,
}: { item: ConfigItem; canEdit: boolean }) {
  const { t } = useTranslation('app')
  const qc = useQueryClient()
  // For secret rows the server ships the value already masked (e.g.
  // `******abcd`). Editing the masked text and saving would clobber
  // the real secret with garbage, so we explicitly require a "Replace"
  // click — that switches the row into "editing" mode where the draft
  // starts empty and the user types the new full value.
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState<string>(
    item.is_secret ? '' : item.value_text,
  )
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const dirty = item.is_secret
    ? (editing && draft.length > 0)
    : draft !== item.value_text

  const save = async (next: string) => {
    if (!canEdit) return
    if (!item.is_secret && next === item.value_text) return
    if (item.is_secret && next.length === 0) return
    setSaving(true); setError(null)
    try {
      await updateConfig(item.key, next)
      qc.invalidateQueries({ queryKey: ['admin', 'config'] })
      setEditing(false)
      setDraft(item.is_secret ? '' : next)
    } catch (e) {
      setError(formatError(e))
      setDraft(item.is_secret ? '' : item.value_text)
    } finally {
      setSaving(false)
    }
  }

  return (
    <li className="px-5 py-3">
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <code className="text-sm font-mono font-semibold">{item.key}</code>
            {item.requires_restart && (
              <span
                className="pill bg-amber-faint text-amber text-2xs"
                title={t('admin.settings.requires_restart_tooltip')}
              >
                {t('admin.settings.requires_restart')}
              </span>
            )}
            {item.is_secret && (
              <span className="pill bg-elevated text-textFaint text-2xs"
                    title={t('admin.settings.secret_tooltip')}>
                {t('admin.settings.secret')}
              </span>
            )}
          </div>
          {(() => {
            const localised = t(`admin.settings.descriptions.${item.key}`, {
              defaultValue: '',
            })
            const desc = localised || item.description
            return desc ? <p className="text-xs text-textDim mt-1">{desc}</p> : null
          })()}
          <p className="text-2xs text-textFaint font-mono mt-1">
            {t('admin.settings.updated', { ago: timeAgo(item.updated_at) })}
          </p>
        </div>
        <div className="w-56 shrink-0">
          {item.value_type === 'bool' ? (
            <BoolSwitch
              value={parseBool(draft || item.value_text)}
              disabled={!canEdit || saving}
              onChange={(v) => {
                const s = v ? 'true' : 'false'
                setDraft(s); save(s)
              }}
            />
          ) : item.is_secret ? (
            // Secret: show the masked current value next to a Replace
            // button. Click → password-style input + Save/Cancel.
            !editing ? (
              <div className="flex items-center gap-2 justify-end">
                <code className="text-xs text-textFaint font-mono">
                  {item.value_text || t('admin.settings.secret_unset')}
                </code>
                <Button size="sm" variant="secondary"
                        disabled={!canEdit}
                        onClick={() => setEditing(true)}>
                  {item.value_text
                    ? t('admin.settings.secret_replace')
                    : t('admin.settings.secret_set')}
                </Button>
              </div>
            ) : (
              <div className="space-y-1">
                <Input
                  type="password"
                  value={draft}
                  disabled={!canEdit || saving}
                  onChange={(e) => setDraft(e.target.value)}
                  placeholder={t('admin.settings.secret_placeholder')}
                  className={cn('font-mono', dirty && 'border-fire')}
                  autoComplete="new-password"
                />
                <div className="flex gap-1 justify-end">
                  <Button size="sm" variant="secondary"
                          disabled={saving}
                          onClick={() => { setEditing(false); setDraft('') }}>
                    {t('admin.settings.cancel')}
                  </Button>
                  <Button size="sm" variant="primary"
                          disabled={!dirty || saving}
                          onClick={() => save(draft)}>
                    {saving ? '…' : t('admin.settings.save')}
                  </Button>
                </div>
              </div>
            )
          ) : (
            <Input
              value={draft}
              disabled={!canEdit || saving}
              onChange={(e) => setDraft(e.target.value)}
              onBlur={() => save(draft)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') (e.target as HTMLInputElement).blur()
                if (e.key === 'Escape') {
                  setDraft(item.value_text);
                  (e.target as HTMLInputElement).blur()
                }
              }}
              className={cn('text-right font-mono', dirty && 'border-fire')}
              type={item.value_type === 'string' ? 'text' : 'number'}
            />
          )}
          {error && <p className="text-2xs text-rose mt-1 break-all">{error}</p>}
        </div>
      </div>
    </li>
  )
}

function parseBool(s: string): boolean {
  return s.toLowerCase() === 'true' || s === '1' || s.toLowerCase() === 'yes'
}
